import * as React from "react";
import "./resources.scss";
import {
  PageHeaderCustomV2,
  PageHeaderCustomV2WhiteWide,
  PageHeaderStandard,
} from "../../components/page-header/page-header";
import { PageTitle } from "../../components/title/title";
import AmazeeIoMetadata from "../../components/metadata";
import AmazeePage from "../../components/layout";
import { AmazeeIOButton } from "../../components/button/button";
import usePreviewData from "../../utils/usePreviewData";
import {
  PrismicRawCaseStudyPage,
  PrismicRawResourceEdge,
  PrismicRawResourcePage,
  Resource,
} from "../../utils/queries/resources";
import { CustomerFeatureCard, CustomerStandardCard } from "./cards";
import { PrismicPageDefinition } from "../../utils/queries/types";
import Linked from "../../utils/linkedItem";
import Modal from "react-modal";
import { QuoteCarousel } from "../../components/reusable/quote-carousel";
import { TypeHomepageV2Quote } from "../../utils/queries/homepagev2";
import Helmet from "react-helmet";
import HomepageLogoList from "../../components/homepage-logos/logo-list";
import classNames from "classnames";

type TypeSubscribeToNewsLetterModalErrorFields =
  | "firstName"
  | "lastName"
  | "email";

const SubscribeToNewsLetterModal: React.FC<{
  isOpen: boolean;
  closeModal: Function;
  location: Location;
}> = ({ isOpen, closeModal, location }) => {
  const customStyles = {
    overlay: {
      backgroundColor: "rgba(0,0,0,.6i)",
    },
    content: {
      backgroundColor: "#273037",
      borderRadius: "0",
      border: "none",
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      color: "white",
      textAlign: "center",
    },
  };

  const [buttonText, setButtonText] = React.useState("Subscribe");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [firstNameError, setFirstNameError] = React.useState(false);
  const [emailError, setEmailError] = React.useState(false);

  const [otherError, setOtherError] = React.useState(null);

  const resetErrors = () => {
    setFirstNameError(false);
    setEmailError(false);
    setOtherError(null);
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
  };

  const updateFirstName = (e) => setFirstName(e.target.value);
  const updateLastName = (e) => setLastName(e.target.value);
  const updateEmail = (e) => setEmail(e.target.value);

  const startSubmit = () => {
    resetErrors();
    let foundError = false;

    if (!firstName || firstName == "") {
      setFirstNameError(true);
      foundError = true;
    }

    if (!email || email == "") {
      setEmailError(true);
      foundError = true;
    }

    if (!foundError) {
      setButtonText("...");

      // Looks good, lets submit
      fetch("/api/subscribe/", {
        method: "POST",
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          page: location ? location.href : null,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })
        // .then(d => d.json())
        .then((val) => {
          if (val.error) {
            if (
              val.details &&
              val.details.errors &&
              val.details.errors.length &&
              val.details.errors[0].message
            ) {
              setOtherError(val.details.errors[0].message);
            } else {
              setOtherError("An error occured");
            }
          } else {
            resetForm();
            setButtonText("Subscribed!");
            // pixelDrop('newsletter');

            setTimeout(closeModal, 1500);
          }
        });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Example Modal"
      style={customStyles}
      className="subscribe-to-newsletter-modal"
      overlayClassName="subscribe-to-newsletter-overlay"
      onRequestClose={closeModal}
    >
      <div className="content">
        <h2>Our Newsletter</h2>
        <div className="close-button" onClick={closeModal}>
          X
        </div>
        <p className="text-white text-left my-4">
          Subscribe to our newletter and stay up to date with the latest news
          about our product, services, events and all things amazee.io.
        </p>
        <form className="flex flex-col">
          <label
            className={classNames({
              error: firstNameError,
            })}
            htmlFor="newsletter__first-name"
          >
            First Name<span className="asterisk">*</span>
          </label>
          <input
            className={classNames({
              error: firstNameError,
            })}
            id="newsletter__first-name"
            value={firstName}
            onChange={updateFirstName}
          />

          <label htmlFor="newsletter__last-name">Last Name</label>
          <input
            id="newsletter__last-name"
            value={lastName}
            onChange={updateLastName}
          />

          <label
            className={classNames({
              error: emailError,
            })}
            htmlFor="newsletter__email"
          >
            Email<span className="asterisk">*</span>
          </label>
          <input
            className={classNames({
              error: emailError,
            })}
            id="newsletter__email"
            value={email}
            onChange={updateEmail}
          />
        </form>

        {otherError && <div className="other-error">{otherError}</div>}
        <AmazeeIOButton
          classes="inverted mt-3 inline-block px-4 py-1 cursor-pointer"
          onClick={startSubmit}
        >
          {buttonText}
        </AmazeeIOButton>

        <div className="text-xs text-left mt-3">
          We will use the personal data you are sharing with us solely for the
          purpose of sending you our newsletter. See more here:{" "}
          <Linked link_to={{ document: { data: { url: "/privacy-policy" } } }}>
            Data Privacy
          </Linked>
        </div>
      </div>
    </Modal>
  );
};

export const ResourceInteruption: React.FC<{
  cta_title: string;
  cta_text: string;
  cta_button_text: string;
  second_cta_button_text: string;
  cta_link: PrismicPageDefinition;
  second_cta_button_link: PrismicPageDefinition;
  topMargin?: boolean;
}> = ({
  cta_title,
  cta_text,
  cta_button_text,
  cta_link,
  second_cta_button_text,
  second_cta_button_link,
  topMargin = true,
}) => {
    return (
      <div
        className={classNames("resource-cta-wrapper no-mb", { tm: topMargin })}
      >
        <div className="resource-cta">
          <div className="row flex justify-center">
            <div className="col-12 col-md-8 flex justify-center flex-col items-center">
              <div className="text-white text-center cta-title">{cta_title}</div>
              <div className="text-white text-center cta-text">{cta_text}</div>
              <div className="flex buttons mt-4">
                <Linked link_to={cta_link}>
                  <AmazeeIOButton classes="inverted inline-block">
                    {cta_button_text}
                  </AmazeeIOButton>
                </Linked>
                <Linked link_to={second_cta_button_link}>
                  <AmazeeIOButton classes="transparent inline-block">
                    {second_cta_button_text}
                  </AmazeeIOButton>
                </Linked>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

export const ResourceInteruptionWide: React.FC<{
  cta_text: string;
  cta_button_text: string;
  second_cta_button_text?: string;
  cta_link: PrismicPageDefinition;
  second_cta_button_link?: PrismicPageDefinition;
  topMargin?: boolean;
}> = ({
  cta_text,
  cta_button_text,
  cta_link,
  second_cta_button_text,
  second_cta_button_link,
  topMargin = true,
}) => {
    return (
      <div
        className={classNames("resource-cta-wrapper no-mb", { tm: topMargin })}
      >
        <div className="resource-cta">
          <div className="row flex justify-center">
            <div className="col-12 col-md-12 flex justify-center flex-col items-center">
              <div className="text-white text-center">{cta_text}</div>
              <div className="flex buttons mt-4">
                <Linked link_to={cta_link}>
                  <AmazeeIOButton classes="inverted inline-block">
                    {cta_button_text}
                  </AmazeeIOButton>
                </Linked>
                {second_cta_button_link && (
                  <Linked link_to={second_cta_button_link}>
                    <AmazeeIOButton classes="transparent inline-block">
                      {second_cta_button_text}
                    </AmazeeIOButton>
                  </Linked>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

export const ResourceInteruptionWideNewsletter: React.FC<{
  location: Location;
  cta_text: string;
  cta_button_text: string;
  second_cta_button_text: string;
  cta_link: PrismicPageDefinition;
  second_cta_button_link: PrismicPageDefinition;
  topMargin?: boolean;
}> = ({
  cta_text,
  cta_button_text,
  cta_link,
  second_cta_button_text,
  second_cta_button_link,
  topMargin = true,
  location,
}) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    return (
      <div
        className={classNames("resource-cta-wrapper no-mb", { tm: topMargin })}
      >
        <div className="resource-cta">
          <div className="row flex justify-center">
            <div className="col-12 col-md-12 flex justify-center flex-col items-center">
              <div className="text-white text-center">{cta_text}</div>
              <div className="flex buttons mt-4">
                <AmazeeIOButton
                  classes="inverted cursor-pointer"
                  onClick={() => setModalIsOpen(true)}
                >
                  {cta_button_text}
                </AmazeeIOButton>
                <SubscribeToNewsLetterModal
                  location={location}
                  isOpen={modalIsOpen}
                  closeModal={() => setModalIsOpen(false)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

export const ResourceInteruptionStatic: React.FC<{
  topMargin?: boolean;
}> = ({ topMargin = true }) => {
  return (
    <div
      className={classNames("resource-cta-wrapper no-mb", { tm: topMargin })}
    >
      <div className="resource-cta">
        <div className="row flex justify-center">
          <div className="col-12 col-md-8 flex justify-center flex-col items-center">
            <div className="text-white text-center">
              Ready to build, run, and scale your applications on a truly open
              source platform?
            </div>
            <div className="flex buttons mt-4">
              <a href={"https://signup.amazee.io"}>
                <AmazeeIOButton classes="inverted inline-block">
                  Get Started
                </AmazeeIOButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ResourceInteruptionGetStartedStatic: React.FC<{
  topMargin?: boolean;
}> = ({ topMargin = true }) => {
  return (
    <div
      className={classNames("resource-cta-wrapper no-mb", { tm: topMargin })}
    >
      <div className="resource-cta">
        <div className="row flex justify-center">
          <div className="col-12 col-md-8 flex justify-center flex-col items-center">
            <div className="text-white text-center">
              Ready to build, run, and scale your applications on a truly open
              source platform?
            </div>
            <div className="flex buttons mt-4">
              <a href={"https://signup.amazee.io"}>
                <AmazeeIOButton classes="inverted inline-block">
                  Get Started
                </AmazeeIOButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ResourceInteruptionAboutUsStatic: React.FC<{
  topMargin?: boolean;
}> = ({ topMargin = true }) => {
  return (
    <div
      className={classNames("resource-cta-wrapper no-mb", { tm: topMargin })}
    >
      <div className="resource-cta">
        <div className="row flex justify-center">
          <div className="col-12 col-md-12 flex justify-center flex-col items-center">
            <div className="text-white text-center">
              Like what you've seen so far?
              <br />
              Check out our current job openings, and apply today.
            </div>
            <div className="flex buttons mt-4">
              <a href={"/careers"}>
                <AmazeeIOButton classes="inverted-dark inline-block">
                  View Careers
                </AmazeeIOButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ResourceInteruptionCareersStatic: React.FC<{
  topMargin?: boolean;
}> = ({ topMargin = true }) => {
  return (
    <div
      className={classNames("resource-cta-wrapper no-mb", { tm: topMargin })}
    >
      <div className="resource-cta">
        <div className="row flex justify-center">
          <div className="col-12 flex justify-center flex-col items-center">
            <p className="text-white text-center mb-0">
              Meet the team and find out more about amazee.io
            </p>
            <div className="flex buttons mt-4">
              <a href={"/about-us"}>
                <AmazeeIOButton classes="inverted-dark inline-block">
                  About Us
                </AmazeeIOButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerPage = ({
  pageContext,
  location,
}: {
  location: Location;
  pageContext: {
    rawResources: PrismicRawResourceEdge[];
    rawPageData: PrismicRawCaseStudyPage;
  };
}) => {
  const { rawResources, rawPageData } = pageContext;
  const resources = rawResources.map(
    (rawResource) => new Resource(rawResource.node.data)
  );
  const [maxResources, setMaxResources] = React.useState(6);

  if (!rawPageData) return null;
  const result = usePreviewData(
    rawPageData.data,
    "allPrismicCustomersPageV2",
    true
  ) as PrismicRawCaseStudyPage["data"];

  const incrementMaxResults = React.useCallback(() => {
    setMaxResources(maxResources + 3);
  }, [maxResources, setMaxResources]);

  const featured = resources.filter(
    (r) => r.featured_on_customer_page && !r.unlisted
  );
  const firstFeatured = featured.length > 0 ? featured[0] : null;

  const renderableResources = resources.filter(
    (r) => !r.featured_on_customer_page && !r.unlisted
  );

  const title = result.meta_title ? result.meta_title : result.title;

  return (
    <AmazeePage location={location}>
      <div id="resources-page">
        <Helmet>
          <link rel="stylesheet" href="/css/carousel-single.min.css" />
          <link rel="stylesheet" href="/css/carousel-multi.min.css" />
        </Helmet>

        <PageTitle title={title} amazeeOnly={true} />
        <AmazeeIoMetadata
          title={result.title}
          page_meta_description={result.page_meta_description}
          page_meta_thumbnail={result.page_meta_thumbnail}
          location={location}
        />

        <PageHeaderStandard
          image={result.hero_image}
          hasSectionDivider={false}
          hasSubnav={false}
          header={result.hero_text.text}
          subheader={result.hero_description}
          withSiteName={false}
          component={PageHeaderCustomV2}
        />

        <div className="container pt-10">
          <div className="row">
            {firstFeatured && <CustomerFeatureCard resource={firstFeatured} />}

            <div className="col-12 text-center" style={{ marginBottom: 60 }}>
              <h2>{result.logo_section_text}</h2>

              <div className="logo-carousel w-full">
                <HomepageLogoList logos={result.logos} grayscale={true} />
              </div>
            </div>
            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {renderableResources.slice(0, 3).map((resource, index) => (
                <CustomerStandardCard resource={resource} key={index} />
              ))}
            </div>

            <div className="col-12 quote-carousel-section mt-16">
              <QuoteCarousel
                withComma={true}
                quotes={result.quotes.map(
                  (quote) =>
                  ({
                    image: quote.logo,
                    text: quote.quote,
                    author_name: quote.author_name,
                    author_title: quote.author_position,
                  } as TypeHomepageV2Quote)
                )}
              />
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-8">
              {renderableResources.slice(3, 6).map((resource, index) => (
                <CustomerStandardCard resource={resource} key={index} />
              ))}
            </div>

            <div className={`grid lg:grid-cols-2 xl:grid-cols-3 gap-8 ${maxResources === 6 ? `` : `mb-8`}`}>
              {renderableResources
                .slice(6, maxResources)
                .map((resource, index) => (
                  <CustomerStandardCard resource={resource} key={index} />
                ))}
            </div>

            {renderableResources.length > maxResources && (
              <div className="col-12 flex justify-center">
                <div
                  className="show-more-button rounded-full text-white"
                  onClick={incrementMaxResults}
                >
                  +
                </div>
              </div>
            )}
          </div>
        </div>
        <ResourceInteruption
          cta_text={result.cta_text}
          cta_button_text={result.cta_button_text}
          cta_link={result.cta_link}
          second_cta_button_text={result.second_cta_button_text}
          second_cta_button_link={result.second_cta_button_link}
        />
      </div>
    </AmazeePage>
  );
};

export default CustomerPage;
